import { validate } from 'rut.js';
import type { ObjectSchema } from 'yup';
import { boolean, number, object, string } from 'yup';

import { isDniFeatureEnabled } from '@/config/featureFlags';
import { DNI_MAX_LENGTH, DNI_MIN_LENGTH } from '@/constants';
import { emailYupValidation } from '@/Domain/entities/Email';
import {
  type IdentificationDocumentType,
  IdentificationDocumentTypes
} from '@/modules/shared/types/IdentificationDocumentType';

import type { AddressGuestFormValues } from '../types/AddressGuestFormValues';
//TODO: improve to move the validation for Address to module/domain.

const PHONE_LENGTH_CHILEAN = 9;
const PHONE_MIN_LENGTH_INTERNATIONAL = 8;
const PHONE_MAX_LENGTH_INTERNATIONAL = 11;
const CUSTOMER_RUT_MAX_LENGTH = 12;
const CUSTOMER_RUT_MIN_LENGTH = 11;
const FIRST_NAME_MAX_LENGTH = 45;
const LAST_NAME_MAX_LENGTH = 45;

const dniEnabled = isDniFeatureEnabled();

export const addressGuestValidator: ObjectSchema<AddressGuestFormValues> =
  object().shape({
    region: number().required('Este campo es obligatorio.'),
    commune: string().required('Este campo es obligatorio.'),
    firstName: string()
      .required('Este campo es obligatorio.')
      .max(FIRST_NAME_MAX_LENGTH, 'Has llegado al máximo de caracteres.'),
    lastName: string()
      .required('Este campo es obligatorio.')
      .max(LAST_NAME_MAX_LENGTH, 'Has llegado al máximo de caracteres.'),

    documentType: string<IdentificationDocumentType>()
      .transform((value) => {
        if (!value) return IdentificationDocumentTypes.RUT;
        if (!dniEnabled && value === IdentificationDocumentTypes.DNI) {
          return IdentificationDocumentTypes.RUT;
        }
        return value;
      })
      .required('Selecciona un tipo de documento.')
      .oneOf(
        dniEnabled
          ? [IdentificationDocumentTypes.RUT, IdentificationDocumentTypes.DNI]
          : [IdentificationDocumentTypes.RUT],
        'Tipo de documento inválido'
      )
      .default(IdentificationDocumentTypes.RUT),

    customerRut: string().when('documentType', {
      is: IdentificationDocumentTypes.RUT,
      then: (schema) =>
        schema
          .required('Este campo es obligatorio.')
          .min(CUSTOMER_RUT_MIN_LENGTH, 'Por favor, ingresa un RUT válido.')
          .max(CUSTOMER_RUT_MAX_LENGTH, 'Por favor, ingresa un RUT válido.')
          .test('is-rut-valid', 'Ingresa un RUT válido', validate),
      otherwise: (schema) =>
        dniEnabled ? schema.notRequired() : schema.strip().notRequired()
    }),

    documentID: string().when('documentType', {
      is: IdentificationDocumentTypes.DNI,
      then: (schema) =>
        dniEnabled
          ? schema
              .transform((value) => value?.trim()) // Trim whitespace
              .required('Este campo es obligatorio.')
              .min(DNI_MIN_LENGTH, 'Por favor, ingresa un DNI válido.')
              .max(
                DNI_MAX_LENGTH,
                `El documento debe tener como máximo ${DNI_MAX_LENGTH} caracteres.`
              )
              .matches(
                /^[A-Za-z0-9]+$/,
                'El documento solo puede contener letras y números.'
              )
          : schema.strip().notRequired(),
      otherwise: (schema) => schema.notRequired()
    }),

    'prefix-phone': string().required('Este campo es obligatorio.'),

    phone: string()
      .trim()
      .required('Este campo es obligatorio.')
      .when('prefix-phone', {
        is: '+56',
        then: (schema) =>
          schema
            .length(PHONE_LENGTH_CHILEAN, 'El teléfono debe tener 9 dígitos.')
            .matches(
              /^9\d{8}$/,
              'Por favor, ingresa un número de teléfono válido.'
            ),
        otherwise: (schema) =>
          schema
            .min(
              PHONE_MIN_LENGTH_INTERNATIONAL,
              'El teléfono debe tener al menos 8 dígitos.'
            )
            .max(
              PHONE_MAX_LENGTH_INTERNATIONAL,
              'El teléfono no puede tener más de 11 dígitos.'
            )
      }),

    email: emailYupValidation,
    'terms-and-cons': boolean()
      .oneOf(
        [true],
        'Para continuar al pago, debes aceptar nuestros Términos y Condiciones y Política de Privacidad de Datos.'
      )
      .defined()
  });
